import { JetView } from 'webix-jet'; // Importing JetView class from webix-jet for creating views
import { getCities } from 'models/cities'; // Importing getCities function from cities model
import { getTags } from 'models/tags'; // Importing getTags function from tags model
import { getPositions } from 'models/positions'; // Importing getPositions function from positions model
import 'webix/photo'; // Importing webix photo module for image handling
import 'webix/tinymce/tinymce'; // Importing TinyMCE editor from webix for rich text editing
import {
    devConfig, // Importing development configuration
    countries, // Importing countries data
    profile, // Importing user profile data
    cashDesks, // Importing cash desks data
    agentList, // Importing agent list data
} from 'config/config'; // Importing configuration settings from config file

/**
 * Class representing the FilterFlujoCajaView.
 * @extends JetView
 */
export default class FilterFlujoCajaView extends JetView {
    /**
     * Configures the view layout and components.
     * @returns {Object} The configuration object for the view.
     */
    config() {
        // Retrieve the localization service from the app
        const _ = this.app.getService('locale')._;
        // Define the left section of the UI
        const left = {
            margin: 5,
            id: 'a1',
            rows: [
                {
                    margin: 15,
                    css: 'on-row',
                    responsive: 'a1',
                    cols: [
                        {
                            // Text input for date selection
                            view: 'text',
                            name: 'Datepicker',
                            id: 'Datepicker',
                            css: 'reportrange',
                            readonly: true,
                            label: "<span class='webix_icon icon-calendar'></span>" + _('Fecha'), // Localized label for datepicker
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Localized placeholder
                        },
                        {
                            // Datepicker component for selecting a date
                            view: 'datepicker',
                            name: 'Date',
                            id: 'Date',
                            stringResult: true, // Returns date as a string
                            minWidth: 200,
                            label: "<span class='webix_icon icon-calendar'></span>" + _('Fecha'), // Localized label
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Localized placeholder
                            value: moment().format('YYYY-MM-DD'), // Default value set to today's date
                            format: '%d %M %Y', // Date format
                            suggest: {
                                type: 'calendar',
                                body: {
                                    maxDate: new Date(), // Maximum selectable date is today
                                },
                            },
                        },
                        {
                            // Rich select for country selection
                            view: 'richselect',
                            name: 'CountrySelect',
                            id: 'CountrySelect',
                            minWidth: 200,
                            localId: 'regions:combo',
                            label: "<span class='icon-language'></span>" + _('País'), // Localized label for country
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Localized placeholder
                            options: {
                                body: {
                                    // Template for displaying country options with flags
                                    template: function (obj) {
                                        return "<i class='flags " + obj.icon + "'></i>" + obj.value;
                                    },
                                    data: countries, // Data source for country options
                                },
                            },
                            // Conditional visibility based on configuration
                            hidden: devConfig.getCountryReport() || !devConfig.getPermAction('filterByCountry', 'Add'),
                            css: 'prm-filterByCountry', // CSS class for styling
                        },
                        {
                            // Second rich select for country selection (readonly)
                            view: 'richselect',
                            name: 'CountrySelect2',
                            minWidth: 200,
                            localId: 'regions:combo',
                            label: "<span class='icon-language'></span>" + _('País'), // Localized label for country
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Localized placeholder
                            value: profile.Settings.ReportCountry, // Default value from profile settings
                            readonly: true, // Set as readonly
                            options: {
                                body: {
                                    // Template for displaying country options with flags
                                    template: function (obj) {
                                        return "<i class='flags " + obj.icon + "'></i>" + obj.value;
                                    },
                                    data: countries, // Data source for country options
                                },
                            },
                            // Conditional visibility based on configuration
                            hidden: !devConfig.getCountryReport() || !devConfig.getPermAction('filterByCountry', 'Add'),
                            css: 'prm-filterByCountry', // CSS class for styling
                        },
                        {
                            // Text input for ticket number
                            view: 'text',
                            name: 'NoTicket',
                            minWidth: 200,
                            label: "<span class='icon-ticket'></span>" + _('No. Ticket'), // Localized label for ticket number
                            labelPosition: 'top',
                            placeholder: _('No. Ticket'), // Localized placeholder
                            invalidMessage: _('No. Ticket is required'), // Validation message
                            type: 'number', // Input type set to number
                        },
                    ],
                },
            ],
        };
        // Define the upper section of the UI
        const upper_section = {
            id: 'a1',
            css: 'father-container',
            height: screen.width < 500 && screen.height < 500 ? 130 : 0, // Conditional height based on screen size
            rows: [
                {
                    responsive: 'a1',
                    margin: 10,
                    cols: [left], // Include the left section
                },
            ],
        };
        // Define the buttons section
        const buttons = {
            margin: 10,
            cols: [
                {},
                {
                    // Button to clear the form
                    view: 'button',
                    value: _('Limpiar'), // Localized label for clear button
                    autowidth: true,
                    click: () => {
                        this.getRoot().clear(); // Clear the form
                    },
                },
                {
                    // Button to submit the form
                    view: 'button',
                    value: _('Consultar'), // Localized label for consult button
                    type: 'form',
                    autowidth: true,
                    click: () => {
                        const filterdata = this.getRoot().getValues(); // Get form values
                        if (devConfig.getCountryReport()) {
                            filterdata.CountrySelect = profile.Settings.ReportCountry; // Set country if report is enabled
                        }
                        delete filterdata['CountrySelect2']; // Remove unused field
                        if (profile.typeDate !== 2) {
                            delete filterdata['Date']; // Remove date if typeDate is not 2
                        } else {
                            if (true) {
                                delete filterdata['Datepicker']; // Remove datepicker if condition is true
                            }
                        }
                        this.app.callEvent('flujoCaja:filter', [filterdata]); // Trigger filter event with data
                        $$('b5').collapse(); // Collapse the accordion section
                    },
                },
            ],
        };
        // Define the accordion section for filters
        const accordion_section = {
            type: 'wide',
            rows: [
                {
                    multi: true,
                    view: 'accordion',
                    css: 'betShop',
                    collapsed: false,
                    type: 'clean',
                    rows: [
                        {
                            header: _('Filtros'), // Localized header for filters
                            id: 'b5',
                            body: {
                                padding: 10,
                                rows: [upper_section, buttons], // Include upper section and buttons
                            },
                        },
                    ],
                },
            ],
        };
        // Define a second buttons section
        const buttons2 = {
            margin: 10,
            cols: [
                {},
                {
                    // Button to clear the second form
                    view: 'button',
                    value: _('Limpiar'), // Localized label for clear button
                    width: 100,
                    click: () => {
                        $$('myForm2').clear(); // Clear the second form
                    },
                },
                {
                    // Button to submit the second form
                    view: 'button',
                    value: _('Consultar'), // Localized label for consult button
                    type: 'form',
                    width: 110,
                    click: () => {
                        const filterdata = $$('myForm2').getValues(); // Get values from the second form
                        if (devConfig.getCountryReport()) {
                            filterdata.CountrySelect = profile.Settings.ReportCountry; // Set country if report is enabled
                        }
                        delete filterdata['CountrySelect2']; // Remove unused field
                        if (profile.typeDate !== 2) {
                            delete filterdata['Date']; // Remove date if typeDate is not 2
                        } else {
                            if (true) {
                                delete filterdata['Datepicker']; // Remove datepicker if condition is true
                            }
                        }
                        this.app.callEvent('flujoCaja:filter', [filterdata]); // Trigger filter event with data
                    },
                },
            ],
        };
        // Define the accordion section for the second form
        const accordion_section3 = {
            padding: 10,
            rows: [upper_section, buttons2], // Include upper section and second buttons
        };
        // Define the second accordion section
        const accordion_section2 = {
            type: 'wide',
            rows: [
                {
                    multi: true,
                    view: 'accordion',
                    css: 'betShop',
                    collapsed: false,
                    type: 'clean',
                    rows: [
                        {
                            header: _('Filtros'), // Localized header for filters
                            id: 'b5',
                            body: {
                                padding: 10,
                                rows: [upper_section, buttons], // Include upper section and buttons
                            },
                        },
                    ],
                },
            ],
        };
        // Define the filter popup window
        const filterPopup = webix.ui({
            view: 'window',
            move: true,
            id: 'filterPopup',
            fullscreen: true,
            head: {
                view: 'toolbar',
                cols: [
                    { view: 'label', label: _('Filtros'), align: 'center' }, // Localized label for filters
                    {
                        // Close icon for the popup
                        view: 'icon',
                        icon: 'wxi-close',
                        click: function () {
                            this.getTopParentView().hide(); // Hide the popup on click
                        },
                    },
                ],
            },
            modal: true, // Modal window
            position: 'center', // Centered position
            body: {
                view: 'form',
                id: 'myForm2', // ID for the second form
                type: 'clean',
                rows: [accordion_section3], // Include accordion section
                rules: {
                    fname: webix.rules.isNotEmpty, // Validation rule for non-empty fields
                },
            },
        });
        // Return the main form structure
        return {
            view: 'form',
            type: 'clean',
            rows: [
                screen.width > 500 && screen.height > 500
                    ? accordion_section2 // Include accordion section if screen size is sufficient
                    : { id: 'right2', height: 1, width: screen.width - 54 }, // Fallback for smaller screens
            ],
            rules: {
                fname: webix.rules.isNotEmpty, // Validation rule for non-empty fields
            },
        }; // Placeholder for the configuration content
    }

    /**
     * Initializes the view and sets up event handlers or data bindings.
     * @param {Object} form - The form object to initialize.
     */
    init(form) {
        webix.ready(function () {
            // Check if the profile typeDate is not equal to 2
            if (profile.typeDate !== 2) {
                // Hide the 'Date' component and show the 'Datepicker' component
                $$('Date').hide();
                $$('Datepicker').show();
            } else {
                // If the condition is true (always true in this case)
                if (true) {
                    // Show the 'Date' component and hide the 'Datepicker' component
                    $$('Date').show();
                    $$('Datepicker').hide();
                }
            }
        });

        // Get the current language setting from the app's locale service
        const lang = this.app.getService('locale').getLang();

        // Attach an event handler for the 'onAfterRender' event of the 'Datepicker' component
        $$('Datepicker').attachEvent('onAfterRender', function () {
            // Set the date modal configuration based on the current language
            devConfig.setDateModal(lang);
        }); // Placeholder for the initialization content
    }
}

// FILE DOCUMENTED
